// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler, HasOnError } from "@insite/client-framework/HandlerCreator";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

interface Props {
    product: ProductModel;
    productsInfo: ProductInfo[];
    pricingLoaded: boolean;
    inventoryLoaded: boolean;
    skipPricingAndInventory: boolean;
    pids: string[];

    error: string;
}

type HandlerType = Handler<{ products: ProductModel[] } & HasOnError, Props>;

export const SetupProductInfos: HandlerType = props => {
    let prodsInfo = [] as ProductInfo[];
    const pids: any = [];
    if (props.parameter.products && props.parameter.products?.length > 0) {
        prodsInfo = props.parameter.products.map((item: any) => {
            pids.push(item.id);
            const productInfo = createFromProduct(item);
            (productInfo as any).erpNumber = item.productNumber;
            return productInfo;
        });
    }
    if (prodsInfo.length > 0) {
        props.productsInfo = prodsInfo;
        props.pids = pids;
    }
};
export const LoadPricing: HandlerType = props => {
    if (props.skipPricingAndInventory) {
        return;
    }

    props.pricingLoaded = false;
    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: props.productsInfo,
            onSuccess: realTimePricing => {
                if (realTimePricing) {
                    props.productsInfo = props.productsInfo.map(item => {
                        const pricing = realTimePricing.realTimePricingResults?.find(
                            o => o.productId === item.productId,
                        );
                        return { ...item, pricing };
                    });
                }
                props.pricingLoaded = true;
            },
            onError: () => {
                props.pricingLoaded = true;
                if (props.productsInfo) {
                    props.productsInfo = props.productsInfo.map(item => {
                        return { ...item, failedToLoadPricing: true };
                    });
                }
                SetProductInfo(props);
            },
            onComplete(realTimePricingProps) {
                if (realTimePricingProps.apiResult) {
                    this.onSuccess?.(realTimePricingProps.apiResult);
                } else if (realTimePricingProps.error) {
                    this.onError?.(realTimePricingProps.error);
                }
            },
        }),
    );
};

export const LoadInventory: HandlerType = async props => {
    if (props.skipPricingAndInventory) {
        return;
    }
    const pids = props.pids;
    if (!pids || pids.length === 0) {
        return;
    }

    props.inventoryLoaded = false;

    props.dispatch(
        loadRealTimeInventory({
            productIds: pids,
            onComplete: realTimeInventoryProps => {
                if (realTimeInventoryProps.error) {
                    if (props.productsInfo) {
                        props.productsInfo &&
                            props.productsInfo.forEach(item => {
                                item.failedToLoadInventory = true;
                            });
                    }
                } else if (props.productsInfo) {
                    props.productsInfo = props.productsInfo.map(item => {
                        const inventory = realTimeInventoryProps.apiResult?.realTimeInventoryResults?.find(
                            o => o.productId === item.productId,
                        );
                        return { ...item, inventory };
                    });
                }
                props.inventoryLoaded = true;
            },
        }),
    );

    await waitFor(() => props.pricingLoaded && props.inventoryLoaded);
};

export const SetProductInfo: HandlerType = props => {
    if (props.productsInfo) {
        let productsInfo = {};
        props.productsInfo.forEach(item => {
            const erpNumber = (item as any).erpNumber;
            productsInfo = {
                [erpNumber]: item,
                ...productsInfo,
            };
        });
        props.dispatch({
            type: "VariantProducts/AddProductsInfo",
            productsInfo,
        } as any);
    }
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (props.error) {
        props.parameter.onError?.(props.error as any);
    }
};

export const chain = [SetupProductInfos, LoadPricing, LoadInventory, SetProductInfo, ExecuteOnErrorCallback];

const getVarinatChildrenProductInfo = createHandlerChainRunner(chain, "getVarinatChildrenProductInfo");
export default getVarinatChildrenProductInfo;
